import * as React from "react"
import { useRef, useState, type ReactNode } from "react"
import { ChevronDownIcon, MenuIcon } from "lucide-react"

import { Button } from "@javascript/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@javascript/components/ui/collapsible"
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger
} from "@javascript/components/ui/sheet"
import {
  DeveloperDocsNav,
  type DeveloperDocsPage
} from "@javascript/components/developer-docs/DeveloperDocsNav"
import { DeveloperDocsToc } from "@javascript/components/developer-docs/DeveloperDocsToc"
import { useDocHeadings } from "@javascript/components/developer-docs/useDocHeadings"

export type { DeveloperDocsPage }

export function DeveloperDocsLayout({
  pages,
  currentHref,
  children
}: {
  pages: DeveloperDocsPage[]
  currentHref: string
  children: ReactNode
}) {
  const contentRef = useRef<HTMLDivElement>(null)
  const { headings, activeId } = useDocHeadings(contentRef)
  const [navOpen, setNavOpen] = useState(false)
  const hasToc = headings.length > 1
  const currentPageIndex = pages.findIndex((page) => page.href === currentHref)
  const previousPage = pages[currentPageIndex - 1]
  const nextPage = pages[currentPageIndex + 1]

  return (
    <div data-slot="developer-docs-layout" className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-6 lg:flex-row lg:px-8">
      <div className="flex items-center lg:hidden">
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetTrigger asChild>
            <Button type="button" variant="outline" size="icon" aria-label="Open documentation navigation">
              <MenuIcon />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" aria-describedby={undefined}>
            <SheetTitle className="px-4 pt-4">Documentation</SheetTitle>
            <DeveloperDocsNav pages={pages} currentHref={currentHref} className="p-4" />
          </SheetContent>
        </Sheet>
      </div>

      <aside className="hidden lg:block lg:w-56 lg:shrink-0">
        <DeveloperDocsNav pages={pages} currentHref={currentHref} />
      </aside>

      <div className="min-w-0 flex-1">
        {hasToc && (
          <Collapsible className="mb-6 lg:hidden">
            <CollapsibleTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="w-full justify-between">
                On this page
                <ChevronDownIcon />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <DeveloperDocsToc headings={headings} activeId={activeId} className="mt-2" />
            </CollapsibleContent>
          </Collapsible>
        )}

        <div ref={contentRef}>{children}</div>

        <nav aria-label="Documentation page navigation" className="mt-8 flex justify-between gap-4 border-t pt-6">
          {previousPage ? (
            <a href={previousPage.href} data-sg-visit="true" className="text-sm font-medium hover:underline">
              Previous
            </a>
          ) : (
            <span />
          )}
          {nextPage && (
            <a href={nextPage.href} data-sg-visit="true" className="text-sm font-medium hover:underline">
              Next
            </a>
          )}
        </nav>
      </div>

      {hasToc && (
        <aside className="hidden lg:block lg:w-56 lg:shrink-0">
          <DeveloperDocsToc headings={headings} activeId={activeId} />
        </aside>
      )}
    </div>
  )
}
